import React, { useState, useEffect, useMemo } from 'react';
import { analyticsService } from '../../services/dataService';

// ─── DATE RANGE HELPERS ──────────────────────────────────────────────────────

function getDateRange(range) {
  const now = new Date();

  if (range === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    const prevEnd = new Date(start); prevEnd.setMilliseconds(-1);
    const prevStart = new Date(prevEnd); prevStart.setHours(0, 0, 0, 0);
    return { start, end: now, prevStart, prevEnd, label: 'Today' };
  }

  if (range === '7d') {
    const start = new Date(now); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    const prevEnd = new Date(start); prevEnd.setMilliseconds(-1);
    const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - 6); prevStart.setHours(0, 0, 0, 0);
    return { start, end: now, prevStart, prevEnd, label: 'Last 7 Days' };
  }

  // month
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { start, end: now, prevStart, prevEnd, label: 'This Month' };
}

function fmtDate(d) {
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

function fmtCurrency(n, symbol) {
  if (n >= 1000) return `${symbol}${(n / 1000).toFixed(1)}k`;
  return `${symbol}${n.toFixed(0)}`;
}

function pctChange(cur, prev) {
  if (prev === 0 && cur === 0) return null;
  if (prev === 0) return 100;
  return ((cur - prev) / prev * 100);
}

// ─── DATA COMPUTATIONS ───────────────────────────────────────────────────────

function computeKPIs(orders) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const totalOrders = paid.length;
  const totalRevenue = paid.reduce((s, o) => s + (parseFloat(o.total_payable || o.total_amount) || 0), 0);
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  const itemQty = {};
  paid.forEach(o => {
    o.order_items?.forEach(oi => {
      if (!oi.menu_items) return;
      const n = oi.menu_items.name;
      itemQty[n] = (itemQty[n] || 0) + (oi.quantity || 1);
    });
  });
  const entries = Object.entries(itemQty).sort((a, b) => b[1] - a[1]);
  const bestItem = entries[0] ? entries[0][0] : null;

  return { totalOrders, totalRevenue, avgOrderValue, bestItem };
}

function computeDailyTrends(orders, start, end) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const days = [];
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(23, 59, 59, 999);
  while (d <= e) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }

  return days.map(day => {
    const ds = day.toDateString();
    const dayOrders = paid.filter(o => new Date(o.paid_at).toDateString() === ds);
    return {
      label: fmtDate(day),
      orders: dayOrders.length,
      revenue: dayOrders.reduce((s, o) => s + (parseFloat(o.total_payable || o.total_amount) || 0), 0)
    };
  });
}

function computeHourlyPattern(orders) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const hrs = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    label: i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`,
    count: 0
  }));
  paid.forEach(o => { hrs[new Date(o.paid_at).getHours()].count++; });
  return hrs;
}

function computeDayOfWeek(orders) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => ({ day: d, idx: i, count: 0 }));
  paid.forEach(o => { days[new Date(o.paid_at).getDay()].count++; });
  return days;
}

function computeItemPerformance(orders) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const items = {};
  paid.forEach(o => {
    o.order_items?.forEach(oi => {
      if (!oi.menu_items) return;
      const { id, name, is_available, categories } = oi.menu_items;
      const catName = categories?.name || 'Uncategorized';
      const qty = oi.quantity || 1;
      const rev = parseFloat(oi.total_price) || (parseFloat(oi.unit_price) || 0) * qty;
      if (!items[id]) items[id] = { id, name, cat: catName, orderCount: 0, quantity: 0, revenue: 0, isAvailable: is_available !== false };
      items[id].orderCount++;
      items[id].quantity += qty;
      items[id].revenue += rev;
    });
  });

  const arr = Object.values(items);
  const totalOrders = arr.reduce((s, i) => s + i.orderCount, 0);
  const meanOrders = arr.length > 0 ? totalOrders / arr.length : 0;
  const totalRev = arr.reduce((s, i) => s + i.revenue, 0) || 1;

  return arr.map(item => ({
    ...item,
    contribution: (item.revenue / totalRev * 100).toFixed(0),
    tag: item.orderCount >= meanOrders * 1.5
      ? 'Trending'
      : item.orderCount <= meanOrders * 0.3 && meanOrders > 0
        ? 'Needs Attention'
        : null
  })).sort((a, b) => b.revenue - a.revenue);
}

function computeCategoryPerformance(orders) {
  const paid = orders.filter(o => o.paid_at && !o.is_invalid);
  const cats = {};
  paid.forEach(o => {
    o.order_items?.forEach(oi => {
      if (!oi.menu_items) return;
      const cat = oi.menu_items.categories?.name || 'Uncategorized';
      const rev = parseFloat(oi.total_price) || 0;
      const qty = oi.quantity || 1;
      if (!cats[cat]) cats[cat] = { name: cat, revenue: 0, quantity: 0 };
      cats[cat].revenue += rev;
      cats[cat].quantity += qty;
    });
  });
  return Object.values(cats).sort((a, b) => b.revenue - a.revenue);
}

// ─── CHART COMPONENTS ────────────────────────────────────────────────────────

const LineChart = ({ data, valueKey, color, currency }) => {
  if (!data || data.length === 0) return <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>No data</div>;
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  const W = 560, H = 140, px = 10, py = 16;
  const plotW = W - 2 * px, plotH = H - 2 * py;
  const n = data.length;
  const pts = data.map((d, i) => ({
    x: px + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW),
    y: py + plotH - (d[valueKey] / maxVal) * plotH
  }));
  const pStr = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `M${pts[0].x.toFixed(1)},${py + plotH} ${pts.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} L${pts[pts.length - 1].x.toFixed(1)},${py + plotH} Z`;

  // show every Nth label to avoid overlap
  const step = Math.max(1, Math.ceil(n / 6));

  return (
    <svg viewBox={`0 0 ${W} ${H + 20}`} style={{ width: '100%', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`g-${valueKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* zero line */}
      <line x1={px} y1={py + plotH} x2={px + plotW} y2={py + plotH} stroke="#e5e7eb" strokeWidth="1" />
      {/* area fill */}
      <path d={area} fill={`url(#g-${valueKey})`} />
      {/* line */}
      <polyline points={pStr} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {/* dots */}
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="white" stroke={color} strokeWidth="2" />
      ))}
      {/* x labels */}
      {data.map((d, i) => i % step === 0 && (
        <text key={i} x={pts[i].x} y={H + 18} textAnchor="middle" fontSize="11" fill="#999">{d.label}</text>
      ))}
    </svg>
  );
};

const HBarChart = ({ data, labelKey, valueKey, color, suffix = '' }) => {
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div style={{ display: 'grid', gap: '8px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '44px', textAlign: 'right', fontSize: '0.8rem', color: '#666', flexShrink: 0 }}>{d[labelKey]}</div>
          <div style={{ flex: 1, height: '20px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${(d[valueKey] / max * 100).toFixed(1)}%`,
              height: '100%',
              background: color,
              borderRadius: '4px',
              transition: 'width 0.6s ease'
            }} />
          </div>
          <div style={{ width: '36px', fontSize: '0.8rem', color: '#333', fontWeight: '600', textAlign: 'right' }}>{d[valueKey]}{suffix}</div>
        </div>
      ))}
    </div>
  );
};

// Vertical bar chart for hourly data
const VBarChart = ({ data, labelKey, valueKey, color }) => {
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  // only show every 3rd label
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '120px', padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
          <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div style={{
              width: '100%',
              height: `${(d[valueKey] / max * 100).toFixed(1)}%`,
              background: color,
              borderRadius: '3px 3px 0 0',
              minHeight: d[valueKey] > 0 ? '3px' : '0',
              transition: 'height 0.6s ease'
            }} />
          </div>
          {i % 3 === 0 && (
            <div style={{ fontSize: '9px', color: '#999', marginTop: '4px', whiteSpace: 'nowrap' }}>{d[labelKey]}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── KPI CARD ────────────────────────────────────────────────────────────────

const KPICard = ({ label, value, delta, sub, accent }) => {
  const isPos = delta !== null && delta >= 0;
  const isNeg = delta !== null && delta < 0;
  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
      borderLeft: `4px solid ${accent || '#FF6B00'}`
    }}>
      <div style={{ fontSize: '0.85rem', color: '#888', fontWeight: '500', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.1, marginBottom: '6px' }}>
        {value}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {delta !== null && (
          <span style={{
            fontSize: '0.8rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
            background: isPos ? '#f0fdf4' : '#fef2f2',
            color: isPos ? '#16a34a' : '#dc2626'
          }}>
            {isPos ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {sub && <span style={{ fontSize: '0.8rem', color: '#aaa' }}>vs prev. period</span>}
      </div>
    </div>
  );
};

// ─── SECTION WRAPPER ─────────────────────────────────────────────────────────

const Section = ({ title, children, action }) => (
  <div style={{ background: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', marginBottom: '24px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
      <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1a1a1a' }}>{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const AnalyticsDashboard = ({ restaurant }) => {
  const [range, setRange] = useState('7d');
  const [orders, setOrders] = useState([]);
  const [prevOrders, setPrevOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemSort, setItemSort] = useState('revenue'); // 'revenue' | 'orders' | 'name'

  const dateRange = useMemo(() => getDateRange(range), [range]);
  const { start, end, prevStart, prevEnd } = dateRange;
  const currency = restaurant?.default_currency || '₹';

  useEffect(() => {
    if (!restaurant?.id) return;
    setLoading(true);
    Promise.all([
      analyticsService.getOrdersForPeriod(restaurant.id, start, end),
      analyticsService.getOrdersForPeriod(restaurant.id, prevStart, prevEnd)
    ]).then(([cur, prev]) => {
      setOrders(cur.data || []);
      setPrevOrders(prev.data || []);
      setLoading(false);
    });
  }, [range, restaurant?.id]);

  // ── Computed metrics ──
  const kpis     = useMemo(() => computeKPIs(orders), [orders]);
  const prevKpis = useMemo(() => computeKPIs(prevOrders), [prevOrders]);
  const daily    = useMemo(() => computeDailyTrends(orders, start, end), [orders, start, end]);
  const hourly   = useMemo(() => computeHourlyPattern(orders), [orders]);
  const dow      = useMemo(() => computeDayOfWeek(orders), [orders]);
  const items    = useMemo(() => computeItemPerformance(orders), [orders]);
  const cats     = useMemo(() => computeCategoryPerformance(orders), [orders]);

  const sortedItems = useMemo(() => {
    const copy = [...items];
    if (itemSort === 'orders') copy.sort((a, b) => b.orderCount - a.orderCount);
    else if (itemSort === 'name') copy.sort((a, b) => a.name.localeCompare(b.name));
    else copy.sort((a, b) => b.revenue - a.revenue);
    return copy;
  }, [items, itemSort]);

  const top5 = items.slice(0, 5);
  const bottom5 = [...items].sort((a, b) => a.revenue - b.revenue).slice(0, 5);

  if (loading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 90px)', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '50px', height: '50px', border: '4px solid #FF4444', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#666', margin: 0, fontWeight: '600' }}>Loading analytics...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const ordersDelta   = pctChange(kpis.totalOrders, prevKpis.totalOrders);
  const revenueDelta  = pctChange(kpis.totalRevenue, prevKpis.totalRevenue);
  const aovDelta      = pctChange(kpis.avgOrderValue, prevKpis.avgOrderValue);
  const peakHour      = hourly.reduce((best, h) => h.count > best.count ? h : best, hourly[0]);
  const peakDay       = dow.reduce((best, d) => d.count > best.count ? d : best, dow[0]);

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: 'calc(100vh - 90px)', padding: '30px 20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* ── Page Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '800', color: '#1a1a1a' }}>Analytics</h1>
            <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
              {fmtDate(start)} – {fmtDate(end)} &nbsp;·&nbsp; {orders.length} orders
            </p>
          </div>

          {/* Date Range Selector */}
          <div style={{ display: 'flex', background: 'white', borderRadius: '12px', padding: '4px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', gap: '4px' }}>
            {[
              { key: 'today', label: 'Today' },
              { key: '7d', label: 'Last 7 Days' },
              { key: 'month', label: 'This Month' }
            ].map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                style={{
                  padding: '9px 18px',
                  border: 'none',
                  borderRadius: '9px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  background: range === r.key ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                  color: range === r.key ? 'white' : '#666',
                  transition: 'all 0.2s ease'
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 1. KPI Summary ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <KPICard
            label="Total Orders"
            value={kpis.totalOrders}
            delta={ordersDelta}
            sub
            accent="#FF4444"
          />
          <KPICard
            label="Total Revenue"
            value={fmtCurrency(kpis.totalRevenue, currency)}
            delta={revenueDelta}
            sub
            accent="#FF6B00"
          />
          <KPICard
            label="Avg Order Value"
            value={`${currency}${kpis.avgOrderValue.toFixed(0)}`}
            delta={aovDelta}
            sub
            accent="#f59e0b"
          />
          <KPICard
            label="Peak Hour"
            value={peakHour?.label || '—'}
            delta={null}
            sub={false}
            accent="#8b5cf6"
          />
          <KPICard
            label="Best Seller"
            value={kpis.bestItem || '—'}
            delta={null}
            sub={false}
            accent="#16a34a"
          />
        </div>

        {/* ── 2. Trends ── */}
        {range !== 'today' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            <Section title="Daily Orders">
              <LineChart data={daily} valueKey="orders" color="#FF4444" currency={currency} />
            </Section>
            <Section title="Daily Revenue">
              <LineChart data={daily} valueKey="revenue" color="#FF6B00" currency={currency} />
            </Section>
          </div>
        )}

        {/* ── 3. Peak Behavior ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <Section title="Orders by Hour">
            {orders.length === 0
              ? <p style={{ color: '#aaa', textAlign: 'center', margin: '20px 0' }}>No orders in this period</p>
              : <VBarChart data={hourly} labelKey="label" valueKey="count" color="linear-gradient(to top, #FF4444, #FF6B00)" />
            }
            <p style={{ margin: '12px 0 0 0', fontSize: '0.85rem', color: '#888', textAlign: 'center' }}>
              {peakHour.count > 0 && `Peak: ${peakHour.label} (${peakHour.count} orders)`}
            </p>
          </Section>
          <Section title="Orders by Day of Week">
            {orders.length === 0
              ? <p style={{ color: '#aaa', textAlign: 'center', margin: '20px 0' }}>No orders in this period</p>
              : <HBarChart data={dow} labelKey="day" valueKey="count" color="linear-gradient(to right, #FF4444, #FF6B00)" />
            }
            <p style={{ margin: '12px 0 0 0', fontSize: '0.85rem', color: '#888', textAlign: 'center' }}>
              {peakDay.count > 0 && `Busiest: ${peakDay.day} (${peakDay.count} orders)`}
            </p>
          </Section>
        </div>

        {/* ── 4. Menu Performance ── */}
        <Section
          title={`Menu Performance (${items.length} items)`}
          action={
            <div style={{ display: 'flex', gap: '6px' }}>
              {[['revenue', 'Revenue'], ['orders', 'Orders'], ['name', 'Name']].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setItemSort(k)}
                  style={{
                    padding: '5px 12px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: '600',
                    background: itemSort === k ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                    color: itemSort === k ? 'white' : '#666'
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          }
        >
          {items.length === 0 ? (
            <p style={{ color: '#aaa', textAlign: 'center', margin: '30px 0' }}>No order data for this period.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                    {['Item', 'Category', 'Orders', 'Qty', 'Revenue', 'Share', 'Status', 'Tag'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Item' || h === 'Category' ? 'left' : 'center', color: '#888', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, i) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f9fafb', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 12px', fontWeight: '600', color: '#1a1a1a', maxWidth: '200px', wordBreak: 'break-word' }}>{item.name}</td>
                      <td style={{ padding: '12px 12px', color: '#666' }}>{item.cat}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontWeight: '700', color: '#333' }}>{item.orderCount}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', color: '#666' }}>{item.quantity}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontWeight: '700', color: '#FF6B00', whiteSpace: 'nowrap', minWidth: '80px' }}>{currency}{item.revenue.toFixed(0)}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', minWidth: '100px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <div style={{ width: '60px', height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${item.contribution}%`, height: '100%', background: 'linear-gradient(to right, #FF4444, #FF6B00)', borderRadius: '3px' }} />
                          </div>
                          <span style={{ color: '#888', fontSize: '0.8rem', width: '32px', textAlign: 'center' }}>{item.contribution}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600',
                          background: item.isAvailable ? '#f0fdf4' : '#fef2f2',
                          color: item.isAvailable ? '#16a34a' : '#dc2626'
                        }}>
                          {item.isAvailable ? 'Active' : 'Out of Stock'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                        {item.tag && (
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                            background: item.tag === 'Trending' ? '#fff7ed' : '#fef9c3',
                            color: item.tag === 'Trending' ? '#ea580c' : '#a16207'
                          }}>
                            {item.tag === 'Trending' ? '🔥 Trending' : '⚠ Needs Attention'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── 5. Category Insights ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>

          {/* Revenue by Category */}
          <Section title="Revenue by Category">
            {cats.length === 0
              ? <p style={{ color: '#aaa', textAlign: 'center', margin: '20px 0' }}>No data</p>
              : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {cats.map((cat, i) => {
                    const maxRev = cats[0].revenue;
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333' }}>{cat.name}</span>
                          <span style={{ fontSize: '0.9rem', color: '#FF6B00', fontWeight: '700' }}>{currency}{cat.revenue.toFixed(0)}</span>
                        </div>
                        <div style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${(cat.revenue / maxRev * 100).toFixed(1)}%`,
                            height: '100%',
                            background: `linear-gradient(to right, #FF4444, #FF6B00)`,
                            borderRadius: '4px',
                            transition: 'width 0.6s ease'
                          }} />
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '2px' }}>{cat.quantity} items sold</div>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </Section>

          {/* Top 5 Items */}
          <Section title="Top 5 Items">
            {top5.length === 0
              ? <p style={{ color: '#aaa', textAlign: 'center', margin: '20px 0' }}>No data</p>
              : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {top5.map((item, i) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: i === 0 ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                        color: i === 0 ? 'white' : '#888',
                        fontSize: '0.75rem', fontWeight: '800', flexShrink: 0
                      }}>
                        {i + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#aaa' }}>{item.orderCount} orders · {currency}{item.revenue.toFixed(0)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </Section>

          {/* Bottom 5 Items */}
          <Section title="Bottom 5 Items">
            {bottom5.length === 0
              ? <p style={{ color: '#aaa', textAlign: 'center', margin: '20px 0' }}>No data</p>
              : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {bottom5.map((item, i) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#fef2f2',
                        color: '#dc2626',
                        fontSize: '0.75rem', fontWeight: '800', flexShrink: 0
                      }}>
                        {i + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#aaa' }}>{item.orderCount} orders · {currency}{item.revenue.toFixed(0)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </Section>
        </div>

      </div>
    </div>
  );
};

export default AnalyticsDashboard;
